import Impostor from '../rendering/Impostor.js';
import { SPELL_PROJECTILES } from './CombatStats.js';

/**
 * ProjectileManager - Manages fireball and other projectile entities.
 * Each projectile is an Impostor (depth-sorted with world geometry)
 * that moves in world space, checks for collisions, and applies damage.
 */
export default class ProjectileManager {
  static _nextId = 0;

  constructor(options = {}) {
    this.world = options.world;
    this.combatManager = options.combatManager;
    this.audioSystem = options.audioSystem;
    this.collisionSystem = options.collisionSystem;

    /** @type {Map<string, Object>} */
    this.projectiles = new Map();

    // Track pending explosion timeouts so they can be cancelled on clearAll()
    // Maps impostor ID → setTimeout handle
    this._explosionTimeouts = new Map();
  }

  /**
   * Spawn a fireball projectile from an attacker.
   * @param {Character} attacker
   */
  spawnFireball(attacker) {
    const id = `proj_${ProjectileManager._nextId++}`;
    const facingRad = (attacker.facingAngle || 0) * Math.PI / 180;

    // Start slightly in front of attacker
    const offsetDist = 0.8;
    let startX = attacker.x + Math.cos(facingRad) * offsetDist;
    let startZ = attacker.z + Math.sin(facingRad) * offsetDist;
    const startY = attacker.y + 0.6; // chest height

    // Clamp spawn position: if the desired spawn point lands inside a wall (e.g.
    // player standing adjacent to a wall) step back along the ray until clear.
    if (this.collisionSystem) {
      this.collisionSystem.ensureIndex();
      const isInsideWall = (x, z) => {
        const cands = this.collisionSystem.queryCuboids({
          minX: x - 0.05, minY: startY - 0.5, minZ: z - 0.05,
          maxX: x + 0.05, maxY: startY + 0.5, maxZ: z + 0.05
        });
        for (const c of cands) {
          if (c.hasNoCollision?.()) continue;
          if (x > c.x && x < c.x + c.width &&
              startY > c.y && startY < c.y + c.height &&
              z > c.z && z < c.z + c.depth) return true;
        }
        return false;
      };
      if (isInsideWall(startX, startZ)) {
        // Step back in 0.1-unit increments to find the first clear position.
        startX = attacker.x;
        startZ = attacker.z;
        for (let d = offsetDist - 0.1; d > 0; d -= 0.1) {
          const tx = attacker.x + Math.cos(facingRad) * d;
          const tz = attacker.z + Math.sin(facingRad) * d;
          if (!isInsideWall(tx, tz)) { startX = tx; startZ = tz; break; }
        }
      }
    }

    // Create an Impostor so fireballs depth-sort with world geometry
    const impostor = new Impostor({
      id,
      x: startX,
      y: startY,
      z: startZ,
      textureUrl: '',
      spriteWidth: 1,
      spriteHeight: 1,
      scale: 0.3
    });
    impostor._skipValidation = true;
    // Mark as projectile so World.addImpostor/removeImpostor skips WorldSync mutations
    // (projectiles sync via the action channel, not the world op channel)
    impostor._isProjectile = true;
    // Sort box: 0.9 × 2 × 0.9 (same footprint as a character sprite) so the SAT
    // depth-sorts fireballs exactly like the player — no special wall-clipping needed.
    // _sortMinY = attacker feet so the Y range matches wall cuboids (floor→ceiling).
    // impostor.y stays at chest height so the visual fireball renders at the right spot.
    impostor.sortWidth   = 0.9; // full sprite footprint — pushed outside walls each frame by _clipSortBoxAgainstWalls
    impostor.sortDepth   = 0.9;
    impostor._sortMinY   = attacker.y;
    impostor._sortHeight = 2;
    // Prevent KDTree subdivision: fragments are persistent across frames, so a moving
    // fireball would accumulate stale wall fragments and corrupt the depth order.
    impostor._isDynamic = true;

    // Restyle element for fireball appearance (replace vegetation sprite look)
    Object.assign(impostor.element.style, {
      backgroundImage: 'none',
      backgroundSize: '',
      backgroundRepeat: '',
      backgroundPosition: 'center',
      imageRendering: 'auto',
      borderRadius: '50%',
      background: 'radial-gradient(circle, #fff 0%, #ffaa00 30%, #ff4400 60%, transparent 100%)',
      boxShadow: '0 0 32px 16px rgba(255, 120, 0, 0.8)',
      pointerEvents: 'none'
    });

    this.world.addImpostor(impostor);

    // Make visible immediately — Impostor starts with display:none and the fast-path
    // render never clears it; only a full sort would, which may not happen soon enough.
    if (this.world.camera) {
      impostor.update(this.world.camera);
    }
    impostor.element.style.display = '';

    // Add attacker's velocity so fireballs aren't slow relative to a running player
    const FIREBALL_SPEED = 12; // base speed in units/s
    const attackerVx = attacker.velocity?.x || 0;
    const attackerVz = attacker.velocity?.z || 0;

    const proj = {
      id,
      impostor,
      vx: Math.cos(facingRad) * FIREBALL_SPEED + attackerVx,
      vz: Math.sin(facingRad) * FIREBALL_SPEED + attackerVz,
      attacker,
      damage: attacker.combatStats ? attacker.combatStats.getEffectiveSpellAttack() * 1.5 : 8,
      knockback: 2.5,
      maxRange: 10,
      traveled: 0,
      age: 0,
      flySound: null  // audio handle for in-flight whoosh
    };

    this.projectiles.set(id, proj);

    // Clip sort box immediately so the first rendered frame is depth-sorted correctly.
    // Without this, the 0.9×0.9 box is unclipped on spawn and may overlap a nearby wall,
    // causing the fireball to flash through the wall for one frame before update() runs.
    this._clipSortBoxAgainstWalls(impostor);

    // Fireball in-flight whoosh — store handle so we can fade volume with the sprite
    if (this.audioSystem) {
      const flyPromise = this.audioSystem.playFireballFly(startX, startY, startZ);
      if (flyPromise && typeof flyPromise.then === 'function') {
        flyPromise.then(handle => { if (handle) proj.flySound = handle; });
      }
    }

    return proj;
  }

  /**
   * Spawn a visual-only fireball for a remote player.
   * Wall collision + explosion are kept for visuals; character hit detection is skipped
   * (the attacker's peer handles combat resolution).
   * @param {Object} data - { x, y, z, angle }
   */
  spawnRemoteFireball(data) {
    const id = `proj_${ProjectileManager._nextId++}`;
    const facingRad = (data.angle || 0) * Math.PI / 180;

    const impostor = new Impostor({
      id,
      x: data.x,
      y: data.y,
      z: data.z,
      textureUrl: '',
      spriteWidth: 1,
      spriteHeight: 1,
      scale: 0.3
    });
    impostor._skipValidation = true;
    impostor._isProjectile = true;
    impostor.sortWidth = 0.9;
    impostor.sortDepth = 0.9;
    impostor._sortMinY = data.y - 0.6;
    impostor._sortHeight = 2;
    impostor._isDynamic = true;

    Object.assign(impostor.element.style, {
      backgroundImage: 'none',
      backgroundSize: '',
      backgroundRepeat: '',
      backgroundPosition: 'center',
      imageRendering: 'auto',
      borderRadius: '50%',
      background: 'radial-gradient(circle, #fff 0%, #ffaa00 30%, #ff4400 60%, transparent 100%)',
      boxShadow: '0 0 32px 16px rgba(255, 120, 0, 0.8)',
      pointerEvents: 'none'
    });

    this.world.addImpostor(impostor);
    if (this.world.camera) impostor.update(this.world.camera);
    impostor.element.style.display = '';

    const FIREBALL_SPEED = 12;
    const proj = {
      id,
      impostor,
      vx: Math.cos(facingRad) * FIREBALL_SPEED,
      vz: Math.sin(facingRad) * FIREBALL_SPEED,
      attacker: null,
      damage: 0,
      knockback: 0,
      maxRange: 10,
      traveled: 0,
      age: 0,
      flySound: null,
      isRemote: true
    };

    this.projectiles.set(id, proj);
    this._clipSortBoxAgainstWalls(impostor);

    if (this.audioSystem) {
      const flyPromise = this.audioSystem.playFireballFly(data.x, data.y, data.z);
      if (flyPromise && typeof flyPromise.then === 'function') {
        flyPromise.then(handle => { if (handle) proj.flySound = handle; });
      }
    }

    return proj;
  }

  // ── Projectile type configs ─────────────────────────────────────────
  static PROJECTILE_TYPES = {
    arrow:     { speed: 16, range: 12, scale: 0.7, damageMul: 1.3, knockback: 2.0 },
    ball:      { speed: 14, range: 8,  scale: 0.2, damageMul: 1.0, knockback: 1.5 },
    boomerang: { speed: 10, range: 6,  scale: 0.4, damageMul: 1.0, knockback: 2.0 },
    icebolt:   { speed: 14, range: 10, scale: 0.25, damageMul: 1.2, knockback: 2.0 },
    poisonbolt:{ speed: 11, range: 9,  scale: 0.25, damageMul: 1.0, knockback: 1.5 },
    arcanebolt:{ speed: 13, range: 10, scale: 0.25, damageMul: 1.1, knockback: 2.0 },
    lightningbolt: { speed: 18, range: 11, scale: 0.3, damageMul: 1.4, knockback: 3.0 }
  };

  /**
   * Spawn a typed projectile (arrow, ball, etc.) from an attacker.
   * @param {Character} attacker
   * @param {string} type - 'arrow', 'ball', 'boomerang'
   * @returns {Object} proj
   */
  spawnProjectile(attacker, type) {
    const cfg = ProjectileManager.PROJECTILE_TYPES[type];
    if (!cfg) return this.spawnFireball(attacker);

    const id = `proj_${ProjectileManager._nextId++}`;
    const facingRad = (attacker.facingAngle || 0) * Math.PI / 180;

    // Start slightly in front of attacker
    const offsetDist = 0.8;
    let startX = attacker.x + Math.cos(facingRad) * offsetDist;
    let startZ = attacker.z + Math.sin(facingRad) * offsetDist;
    const startY = attacker.y + 0.6;

    // Wall spawn clamping (same logic as spawnFireball)
    if (this.collisionSystem) {
      this.collisionSystem.ensureIndex();
      const isInsideWall = (x, z) => {
        const cands = this.collisionSystem.queryCuboids({
          minX: x - 0.05, minY: startY - 0.5, minZ: z - 0.05,
          maxX: x + 0.05, maxY: startY + 0.5, maxZ: z + 0.05
        });
        for (const c of cands) {
          if (c.hasNoCollision?.()) continue;
          if (x > c.x && x < c.x + c.width &&
              startY > c.y && startY < c.y + c.height &&
              z > c.z && z < c.z + c.depth) return true;
        }
        return false;
      };
      if (isInsideWall(startX, startZ)) {
        startX = attacker.x;
        startZ = attacker.z;
        for (let d = offsetDist - 0.1; d > 0; d -= 0.1) {
          const tx = attacker.x + Math.cos(facingRad) * d;
          const tz = attacker.z + Math.sin(facingRad) * d;
          if (!isInsideWall(tx, tz)) { startX = tx; startZ = tz; break; }
        }
      }
    }

    const impostor = new Impostor({
      id,
      x: startX, y: startY, z: startZ,
      textureUrl: '',
      spriteWidth: 1, spriteHeight: 1,
      scale: cfg.scale
    });
    impostor._skipValidation = true;
    impostor._isProjectile = true;
    impostor.sortWidth = 0.9;
    impostor.sortDepth = 0.9;
    impostor._sortMinY = attacker.y;
    impostor._sortHeight = 2;
    impostor._isDynamic = true;

    // Compute velocities first so we can derive screen angle for the arrow texture
    const attackerVx = attacker.velocity?.x || 0;
    const attackerVz = attacker.velocity?.z || 0;
    const projVx = Math.cos(facingRad) * cfg.speed + attackerVx;
    const projVz = Math.sin(facingRad) * cfg.speed + attackerVz;

    // Apply projectile-type visual (use screen-space angle for directional types)
    const camera = this.world.camera;
    const screenAngle = ProjectileManager._getArrowScreenAngle(projVx, projVz, camera);
    this._styleProjectile(impostor.element, type, screenAngle);

    this.world.addImpostor(impostor);
    if (camera) impostor.update(camera);
    impostor.element.style.display = '';

    const proj = {
      id, impostor,
      vx: projVx,
      vz: projVz,
      attacker,
      damage: attacker.combatStats
        ? (SPELL_PROJECTILES.includes(type)
            ? attacker.combatStats.getEffectiveSpellAttack()
            : attacker.combatStats.getEffectiveRangedAttack()) * cfg.damageMul
        : 5,
      knockback: cfg.knockback,
      maxRange: cfg.range,
      traveled: 0, age: 0,
      flySound: null,
      projectileType: type,
      _lastCameraAngle: camera?.angle ?? 0
    };

    this.projectiles.set(id, proj);
    this._clipSortBoxAgainstWalls(impostor);

    // Reuse fireball fly sound for now (quieter for arrows)
    if (this.audioSystem) {
      const flyPromise = this.audioSystem.playFireballFly(startX, startY, startZ);
      if (flyPromise && typeof flyPromise.then === 'function') {
        flyPromise.then(handle => { if (handle) proj.flySound = handle; });
      }
    }

    return proj;
  }

  /**
   * Spawn a visual-only typed projectile for a remote player.
   * @param {Object} data - { x, y, z, angle, projectileType }
   */
  spawnRemoteProjectile(data) {
    const type = data.projectileType || 'arrow';
    const cfg = ProjectileManager.PROJECTILE_TYPES[type];
    if (!cfg) return this.spawnRemoteFireball(data);

    const id = `proj_${ProjectileManager._nextId++}`;
    const facingRad = (data.angle || 0) * Math.PI / 180;

    const impostor = new Impostor({
      id,
      x: data.x, y: data.y, z: data.z,
      textureUrl: '',
      spriteWidth: 1, spriteHeight: 1,
      scale: cfg.scale
    });
    impostor._skipValidation = true;
    impostor._isProjectile = true;
    impostor.sortWidth = 0.9;
    impostor.sortDepth = 0.9;
    impostor._sortMinY = data.y - 0.6;
    impostor._sortHeight = 2;
    impostor._isDynamic = true;

    const projVx = Math.cos(facingRad) * cfg.speed;
    const projVz = Math.sin(facingRad) * cfg.speed;
    const camera = this.world.camera;
    const screenAngle = ProjectileManager._getArrowScreenAngle(projVx, projVz, camera);
    this._styleProjectile(impostor.element, type, screenAngle);

    this.world.addImpostor(impostor);
    if (camera) impostor.update(camera);
    impostor.element.style.display = '';

    const proj = {
      id, impostor,
      vx: projVx,
      vz: projVz,
      attacker: null, damage: 0, knockback: 0,
      maxRange: cfg.range,
      traveled: 0, age: 0,
      flySound: null,
      isRemote: true,
      projectileType: type,
      _lastCameraAngle: camera?.angle ?? 0
    };

    this.projectiles.set(id, proj);
    this._clipSortBoxAgainstWalls(impostor);

    if (this.audioSystem) {
      const flyPromise = this.audioSystem.playFireballFly(data.x, data.y, data.z);
      if (flyPromise && typeof flyPromise.then === 'function') {
        flyPromise.then(handle => { if (handle) proj.flySound = handle; });
      }
    }

    return proj;
  }

  /**
   * Compute the screen-space angle for an arrow given its world velocity
   * and the current camera state. Accounts for camera rotation and isometric skew.
   * @param {number} vx - World-space X velocity
   * @param {number} vz - World-space Z velocity
   * @param {Object} camera - Camera with .angle (degrees) and .skewAngle
   * @returns {number} Screen-space angle in radians
   * @private
   */
  static _getArrowScreenAngle(vx, vz, camera) {
    if (!camera) return Math.atan2(vz, vx);
    const camRad = (camera.angle || 0) * Math.PI / 180;
    const cos = Math.cos(camRad);
    const sin = Math.sin(camRad);
    // Impostor.update negates Z: worldZ = -z * cubeSize
    const worldDX = vx;
    const worldDZ = -vz;
    const screenDX = worldDX * cos - worldDZ * sin;
    // yScale from skew (same formula as Transform.calculateYScale)
    const pitchRad = ((camera.skewAngle || 0.3) * Math.PI) / 4;
    const yScale = Math.sin(pitchRad);
    const screenDY = (worldDX * sin + worldDZ * cos) * yScale;
    return Math.atan2(screenDY, screenDX);
  }

  /**
   * Apply projectile-type visual styling to an element.
   * @param {HTMLElement} el
   * @param {string} type - 'arrow', 'ball', 'boomerang'
   * @param {number} screenAngle - Screen-space angle in radians for directional projectiles
   * @private
   */
  _styleProjectile(el, type, screenAngle) {
    if (type === 'arrow') {
      // Arrow: canvas-generated sprite rotated to screen-space direction
      const texUrl = ProjectileManager._getArrowTexture(screenAngle);
      Object.assign(el.style, {
        backgroundImage: `url(${texUrl})`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        imageRendering: 'pixelated',
        borderRadius: '0',
        background: '',
        boxShadow: 'none',
        pointerEvents: 'none'
      });
      // Re-set background-image after clearing background shorthand
      el.style.backgroundImage = `url(${texUrl})`;
      el.style.backgroundSize = 'contain';
      el.style.backgroundRepeat = 'no-repeat';
      el.style.backgroundPosition = 'center';
    } else if (type === 'ball') {
      // Small stone/ball
      Object.assign(el.style, {
        backgroundImage: 'none',
        backgroundSize: '',
        backgroundRepeat: '',
        backgroundPosition: 'center',
        imageRendering: 'auto',
        borderRadius: '50%',
        background: 'radial-gradient(circle, #b8a88a 0%, #8B7355 60%, #5a4a3a 100%)',
        boxShadow: '0 0 4px 2px rgba(0, 0, 0, 0.3)',
        pointerEvents: 'none'
      });
    } else if (type === 'boomerang') {
      // Boomerang: brown curved shape
      Object.assign(el.style, {
        backgroundImage: 'none',
        backgroundSize: '',
        backgroundRepeat: '',
        backgroundPosition: 'center',
        imageRendering: 'auto',
        borderRadius: '40%',
        background: 'radial-gradient(circle, #c4a060 0%, #8B6914 70%, #5a4a2a 100%)',
        boxShadow: '0 0 6px 3px rgba(139, 105, 20, 0.4)',
        pointerEvents: 'none'
      });
    } else if (type === 'icebolt') {
      // Ice bolt: blue-white crystalline shard with frosty glow
      Object.assign(el.style, {
        backgroundImage: 'none',
        backgroundSize: '',
        backgroundRepeat: '',
        backgroundPosition: 'center',
        imageRendering: 'auto',
        borderRadius: '30% 50% 30% 50%',
        background: 'radial-gradient(circle, #ffffff 0%, #88ddff 35%, #3399ff 65%, transparent 100%)',
        boxShadow: '0 0 24px 12px rgba(100, 180, 255, 0.7)',
        pointerEvents: 'none'
      });
    } else if (type === 'poisonbolt') {
      // Poison bolt: green toxic glob with sickly aura
      Object.assign(el.style, {
        backgroundImage: 'none',
        backgroundSize: '',
        backgroundRepeat: '',
        backgroundPosition: 'center',
        imageRendering: 'auto',
        borderRadius: '50%',
        background: 'radial-gradient(circle, #ccff66 0%, #44cc00 40%, #227700 70%, transparent 100%)',
        boxShadow: '0 0 20px 10px rgba(50, 200, 50, 0.6)',
        pointerEvents: 'none'
      });
    } else if (type === 'arcanebolt') {
      // Arcane bolt: purple/violet orb with magical sparkle
      Object.assign(el.style, {
        backgroundImage: 'none',
        backgroundSize: '',
        backgroundRepeat: '',
        backgroundPosition: 'center',
        imageRendering: 'auto',
        borderRadius: '50%',
        background: 'radial-gradient(circle, #ffffff 0%, #cc88ff 30%, #8833dd 60%, transparent 100%)',
        boxShadow: '0 0 28px 14px rgba(160, 80, 255, 0.7)',
        pointerEvents: 'none'
      });
    } else if (type === 'lightningbolt') {
      // Lightning bolt: bright yellow-white crackling energy
      Object.assign(el.style, {
        backgroundImage: 'none',
        backgroundSize: '',
        backgroundRepeat: '',
        backgroundPosition: 'center',
        imageRendering: 'auto',
        borderRadius: '40% 50% 40% 50%',
        background: 'radial-gradient(circle, #ffffff 0%, #ffff44 30%, #ffcc00 55%, transparent 100%)',
        boxShadow: '0 0 32px 16px rgba(255, 240, 60, 0.8)',
        pointerEvents: 'none'
      });
    }
  }

  // ── Arrow texture cache ───────────────────────────────────────────
  // Pre-rendered arrow sprites at 8 angles (45-degree increments).
  // Each is a small canvas converted to a blob URL.
  static _arrowCache = new Map(); // angleKey → blobUrl

  /**
   * Get (or create) a cached arrow texture for the given screen-space angle.
   * Quantizes to 16 directions (22.5-degree increments) for smoother rotation.
   * @param {number} screenRad - Screen-space angle in radians
   * @returns {string} data URL
   * @private
   */
  static _getArrowTexture(screenRad) {
    // Quantize to nearest 22.5 degrees (16 directions)
    const step = Math.PI / 8;
    const quantized = Math.round(screenRad / step) * step;
    const key = quantized.toFixed(3);

    if (ProjectileManager._arrowCache.has(key)) {
      return ProjectileManager._arrowCache.get(key);
    }

    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    ctx.translate(size / 2, size / 2);
    // Rotate canvas so arrow points in screen-space travel direction
    ctx.rotate(quantized);

    // Draw arrow pointing right (east = 0 radians)
    ctx.strokeStyle = '#4a3520';
    ctx.fillStyle = '#4a3520';
    ctx.lineWidth = 3;

    // Shaft
    ctx.beginPath();
    ctx.moveTo(-22, 0);
    ctx.lineTo(16, 0);
    ctx.stroke();

    // Arrowhead
    ctx.beginPath();
    ctx.moveTo(24, 0);
    ctx.lineTo(14, -6);
    ctx.lineTo(16, 0);
    ctx.lineTo(14, 6);
    ctx.closePath();
    ctx.fill();

    // Fletching
    ctx.strokeStyle = '#8B7355';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(-22, 0);
    ctx.lineTo(-26, -5);
    ctx.moveTo(-22, 0);
    ctx.lineTo(-26, 5);
    ctx.moveTo(-18, 0);
    ctx.lineTo(-22, -4);
    ctx.moveTo(-18, 0);
    ctx.lineTo(-22, 4);
    ctx.stroke();

    const url = canvas.toDataURL('image/png');
    ProjectileManager._arrowCache.set(key, url);
    return url;
  }

  /**
   * Update all projectiles. Call each frame.
   * @param {number} deltaTime
   * @param {Object} camera
   */
  update(deltaTime, camera) {
    for (const [id, proj] of this.projectiles) {
      // Guard: impostor removed externally (e.g. world clear/fromJSON)
      if (!proj.impostor.element) {
        this.projectiles.delete(id);
        if (proj.flySound) { proj.flySound.stop(); proj.flySound = null; }
        continue;
      }

      proj.age += deltaTime;

      // Move (update impostor world position directly)
      const dx = proj.vx * deltaTime;
      const dz = proj.vz * deltaTime;
      proj.impostor.x += dx;
      proj.impostor.z += dz;
      proj.traveled += Math.sqrt(dx * dx + dz * dz);

      // Update arrow texture when camera rotates (screen-space angle changes)
      if (proj.projectileType === 'arrow' && camera &&
          proj._lastCameraAngle !== undefined && camera.angle !== proj._lastCameraAngle) {
        proj._lastCameraAngle = camera.angle;
        const screenAngle = ProjectileManager._getArrowScreenAngle(proj.vx, proj.vz, camera);
        const texUrl = ProjectileManager._getArrowTexture(screenAngle);
        proj.impostor.element.style.backgroundImage = `url(${texUrl})`;
      }

      // Fade out over the last 30% of max range (visual + audio)
      const fadeStart = proj.maxRange * 0.7;
      if (proj.traveled > fadeStart) {
        const t = (proj.traveled - fadeStart) / (proj.maxRange - fadeStart);
        const opacity = Math.max(0, 1 - t);
        proj.impostor.element.style.opacity = opacity;
        if (proj.flySound) proj.flySound.setGain(opacity);
      }

      // Check max range / lifetime
      if (proj.traveled > proj.maxRange || proj.age > 3) {
        this._destroyProjectile(id, proj);
        continue;
      }

      // Check wall collision (before character hit check)
      const hitWall = this._checkWallCollision(proj);
      if (hitWall) {
        this._destroyProjectile(id, proj, true, hitWall);
        continue;
      }

      // Clip the SAT sort box away from any overlapping wall so the depth-sort
      // gives a clean separation verdict rather than relying on the tiebreaker.
      // The 0.9-wide box can extend into a wall when the fireball is close to one.
      this._clipSortBoxAgainstWalls(proj.impostor);

      // Check collision with characters (skip for remote fireballs — attacker's peer handles combat)
      if (!proj.isRemote) {
        const hit = this._checkHit(proj);
        if (hit) {
          this._onHit(proj, hit);
          this._destroyProjectile(id, proj, true);
          continue;
        }
      }
    }

    // Trigger a full depth sort every frame while projectiles are in flight so DOM
    // order stays correct as they move through the scene.
    if (this.projectiles.size > 0 && this.world) {
      this.world.requestFullSort();
    }
  }

  /**
   * Check if projectile collides with a solid wall/cuboid.
   * @returns {Object|null} The hit cuboid, or null if no collision.
   * @private
   */
  _checkWallCollision(proj) {
    if (!this.collisionSystem) return null;
    this.collisionSystem.ensureIndex();

    const imp = proj.impostor;
    const r = 0.2; // collision radius
    const aabb = {
      minX: imp.x - r,
      minY: imp.y - r,
      minZ: imp.z - r,
      maxX: imp.x + r,
      maxY: imp.y + r,
      maxZ: imp.z + r
    };

    const candidates = this.collisionSystem.queryCuboids(aabb);
    for (const cuboid of candidates) {
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;
      // Check AABB overlap
      if (imp.x >= cuboid.x && imp.x <= cuboid.x + cuboid.width &&
          imp.y >= cuboid.y && imp.y <= cuboid.y + cuboid.height &&
          imp.z >= cuboid.z && imp.z <= cuboid.z + cuboid.depth) {
        return cuboid;
      }
    }
    return null;
  }

  /**
   * Clip the impostor's SAT sort box against every nearby solid cuboid so it
   * never overlaps any wall geometry (with a small gap).  Uses minimum-penetration
   * to find the best clipping edge for each overlapping cuboid — same strategy as
   * the vegetation sort-box clipping in DungeonStreamer.
   *
   * Call every frame for flying fireballs, and once after explosion setup.
   * @param {Object} imp - Impostor whose sort box to clip
   * @private
   */
  _clipSortBoxAgainstWalls(imp) {
    if (!this.collisionSystem) return;
    this.collisionSystem.ensureIndex();

    const GAP = 0.05;
    const FULL = 0.9;
    const halfW = FULL / 2; // 0.45
    const halfD = FULL / 2; // 0.45

    // Reset sort box to the full sprite footprint every frame, centred at the visual
    // position. The sort centre will only be shifted if the box overlaps a wall.
    imp.sortWidth   = FULL;
    imp.sortDepth   = FULL;
    imp.sortCenterX = null;
    imp.sortCenterZ = null;

    // Query a region slightly larger than the sort box.
    const qr = halfW + 0.1;
    const candidates = this.collisionSystem.queryCuboids({
      minX: imp.x - qr, minY: imp.y - 1, minZ: imp.z - qr,
      maxX: imp.x + qr, maxY: imp.y + 1, maxZ: imp.z + qr
    });

    // Find the overlapping wall whose sort-box penetration is shallowest — that is
    // the face the fireball is actually approaching.  We skip floors, ceilings and
    // short ledges by requiring the fireball's visual Y to lie inside the cuboid's
    // Y range; without this, a floor cuboid that spans the whole level would be
    // picked first (tiny X-penetration = shallowest) and the sort centre would be
    // pushed to an absurd position like X = −0.5.
    let bestPen = Infinity, bestCuboid = null;

    for (const cuboid of candidates) {
      if (cuboid.hasNoCollision?.()) continue;
      // Ignore cuboids the fireball is above or below (floors, ceilings, ledges).
      if (imp.y <= cuboid.y || imp.y >= cuboid.y + cuboid.height) continue;

      const cMinX = cuboid.x, cMaxX = cuboid.x + cuboid.width;
      const cMinZ = cuboid.z, cMaxZ = cuboid.z + cuboid.depth;

      // Skip if the 0.9×0.9 sort box (centred at imp.x/z) doesn't overlap.
      if (imp.x + halfW <= cMinX || imp.x - halfW >= cMaxX) continue;
      if (imp.z + halfD <= cMinZ || imp.z - halfD >= cMaxZ) continue;

      const pen = Math.min(
        (imp.x + halfW) - cMinX,
        cMaxX - (imp.x - halfW),
        (imp.z + halfD) - cMinZ,
        cMaxZ - (imp.z - halfD)
      );
      if (pen < bestPen) { bestPen = pen; bestCuboid = cuboid; }
    }

    if (bestCuboid) {
      const cMinX = bestCuboid.x, cMaxX = bestCuboid.x + bestCuboid.width;
      const cMinZ = bestCuboid.z, cMaxZ = bestCuboid.z + bestCuboid.depth;

      const penRight = (imp.x + halfW) - cMinX;
      const penLeft  = cMaxX - (imp.x - halfW);
      const penBack  = (imp.z + halfD) - cMinZ;
      const penFront = cMaxZ - (imp.z - halfD);

      // Push the sort CENTRE completely outside the minimum-penetration face so the
      // 0.9×0.9 sort box no longer overlaps this wall.  Visual position unchanged.
      const minPen = Math.min(penRight, penLeft, penBack, penFront);
      if      (minPen === penRight) imp.sortCenterX = cMinX - GAP - halfW;
      else if (minPen === penLeft)  imp.sortCenterX = cMaxX + GAP + halfW;
      else if (minPen === penBack)  imp.sortCenterZ = cMinZ - GAP - halfD;
      else                          imp.sortCenterZ = cMaxZ + GAP + halfD;
    }
  }

  /**
   * Snap the explosion impostor's visual position to the hit wall face and place its
   * SAT sort box entirely in the open space outside that face.
   *
   * @param {Object} imp       - Impostor to snap
   * @param {Object} cuboid    - The wall cuboid that was hit
   * @param {number} [vx=0]   - Projectile X velocity (used to pick the correct face)
   * @param {number} [vz=0]   - Projectile Z velocity
   * @private
   */
  _setSortCenterOutsideWall(imp, cuboid, vx = 0, vz = 0) {
    const GAP   = 0.05;
    const halfS = imp.scale * 0.5;
    const FULL  = 0.9; // standard sort footprint (matches spawnFireball)

    // Determine entry face using a hybrid of velocity and penetration depth:
    //   • Velocity sign picks which face to consider on each axis (entry vs exit).
    //   • Penetration depth comparison selects the axis (the face with the smallest
    //     penetration = the one most recently crossed = the true entry face).
    // This handles grazing-angle hits correctly: a fireball moving mostly along Z
    // that barely clips an X face will have small penX and large penZ, so X wins.

    // X-axis: entry face and penetration based on velocity sign
    let penX, sortCX;
    if (vx >= 0) {
      penX   = imp.x - cuboid.x;                      // entered from min-X side
      sortCX = cuboid.x - GAP - halfS;
    } else {
      penX   = (cuboid.x + cuboid.width) - imp.x;     // entered from max-X side
      sortCX = cuboid.x + cuboid.width + GAP + halfS;
    }

    // Z-axis: entry face and penetration based on velocity sign
    let penZ, sortCZ;
    if (vz >= 0) {
      penZ   = imp.z - cuboid.z;                      // entered from min-Z side
      sortCZ = cuboid.z - GAP - halfS;
    } else {
      penZ   = (cuboid.z + cuboid.depth) - imp.z;     // entered from max-Z side
      sortCZ = cuboid.z + cuboid.depth + GAP + halfS;
    }

    // Clamp to zero: floating-point can make the fireball land exactly on a face
    // boundary, giving a tiny negative penetration that would corrupt the comparison.
    penX = Math.max(0, penX);
    penZ = Math.max(0, penZ);

    // Pick the axis whose entry face has the smaller penetration depth.
    // Visual position (imp.x/z) is snapped to the wall face — the actual intersection
    // point — so the explosion renders right where the fireball hit.
    // Sort center is pushed further outside the wall (by GAP + halfS) so the SAT
    // sort box is entirely clear of the wall geometry for a clean depth-sort verdict.
    // Also reset the orthogonal sort dimension to FULL so it is not left at whatever
    // _clipSortBoxAgainstWalls shrank it to during flight.
    if (penZ <= penX) {
      imp.z           = vz >= 0 ? cuboid.z : cuboid.z + cuboid.depth; // snap to face
      imp.sortCenterZ = sortCZ;  // outside the face by GAP + halfS
      imp.sortDepth   = halfS * 2;
      imp.sortWidth   = FULL;   // reset from any flight-clipped value
      imp.sortCenterX = null;
    } else {
      imp.x           = vx >= 0 ? cuboid.x : cuboid.x + cuboid.width; // snap to face
      imp.sortCenterX = sortCX;  // outside the face by GAP + halfS
      imp.sortWidth   = halfS * 2;
      imp.sortDepth   = FULL;   // reset from any flight-clipped value
      imp.sortCenterZ = null;
    }
  }

  /**
   * Check if projectile hit any valid target.
   * @private
   */
  _checkHit(proj) {
    if (!this.world) return null;

    const imp = proj.impostor;
    for (const target of this.world.characters.values()) {
      if (target === proj.attacker) continue;
      if (!target.combatStats || target.combatStats.isDead) continue;
      if (target.combatStats.isInvulnerable) continue;
      // Faction check
      if (proj.attacker.isNPC === target.isNPC) continue;

      const dx = target.x - imp.x;
      const dz = target.z - imp.z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist < 1.0) {
        return target;
      }
    }
    return null;
  }

  /**
   * Handle projectile hitting a target.
   * @private
   */
  _onHit(proj, target) {
    const stats = target.combatStats;
    const finalDamage = Math.max(1, proj.damage - stats.getEffectiveDefense());

    // In multiplayer: non-authority peers don't apply projectile damage to NPCs.
    // Send damage to authority who applies it via 4Hz health broadcasts.
    // Same pattern as CombatManager._applyDamage.
    const nm = window.game?.networkManager;
    if (nm?.isConnected && target.isNPC && !window.game?.npcSync?._isAuthority) {
      const dim = window.game?.dungeonInstanceManager;
      nm.broadcastAction({
        type: 'npc-damage',
        npcId: target.id,
        damage: finalDamage,
        ax: proj.attacker?.x ?? proj.impostor.x,
        az: proj.attacker?.z ?? proj.impostor.z,
        levelIndex: dim?._isInDungeon ? dim._myLevel : -1
      });
      // Local visual/audio feedback only
      if (target.sprite) target.sprite._hitFlashTimer = 0.1;
      if (this.audioSystem) this.audioSystem.playHitSound(target, proj.attacker, finalDamage, false);
      if (this.combatManager?.hudManager) this.combatManager.hudManager.showDamageNumber(target, finalDamage);
      return;
    }

    // In multiplayer: when authority projectile hits a remote player, broadcast
    // damage to the owning peer. Same pattern as CombatManager._applyDamage.
    if (nm?.isConnected && !target.isNPC && target.isRemote) {
      const targetPeerId = target.id.replace('remote_', '');
      const dim = window.game?.dungeonInstanceManager;
      nm.broadcastAction({
        type: 'player-damage',
        targetPeerId,
        damage: finalDamage,
        ax: proj.impostor.x,
        az: proj.impostor.z,
        knockback: proj.knockback || 2.5,
        levelIndex: dim?._isInDungeon ? dim._myLevel : -1
      });
      if (target.sprite) target.sprite._hitFlashTimer = 0.1;
      if (this.audioSystem) this.audioSystem.playHitSound(target, proj.attacker, finalDamage, false);
      if (this.combatManager?.hudManager) this.combatManager.hudManager.showDamageNumber(target, finalDamage);
      return;
    }

    // Apply damage
    stats.health -= finalDamage;

    // Block health regen for 5 seconds after taking damage
    stats.healthRegenDelay = 5.0;

    // Update NPC nameplate health bar
    if (target._nameplateHpFill) {
      const pct = Math.max(0, stats.health / stats.maxHealth) * 100;
      target._nameplateHpFill.style.width = `${pct}%`;
    }

    // Update player floating health bar (create lazily if needed)
    if (!target.isNPC) {
      if (!target._floatingHpFill && target.sprite?.element) {
        const hpBar = document.createElement('div');
        hpBar.className = 'player-hp-bar';
        hpBar.innerHTML = '<div class="player-hp-fill"></div>';
        target.sprite.element.appendChild(hpBar);
        target._floatingHpBar = hpBar;
        target._floatingHpFill = hpBar.querySelector('.player-hp-fill');
      }
      if (target._floatingHpFill) {
        const pct = Math.max(0, stats.health / stats.maxHealth) * 100;
        target._floatingHpFill.style.width = `${pct}%`;
        target._floatingHpFill.style.backgroundColor = pct > 60 ? '#22c55e' : pct > 30 ? '#eab308' : '#ef4444';
        if (target._floatingHpBar) target._floatingHpBar.style.display = 'block';
      }
    }

    // I-frames
    stats.isInvulnerable = true;
    stats.invulnerabilityTimer = stats.invulnerabilityDuration;

    // Knockback in projectile direction
    const speed = Math.sqrt(proj.vx * proj.vx + proj.vz * proj.vz) || 1;
    stats.knockbackVelocity.x = (proj.vx / speed) * proj.knockback;
    stats.knockbackVelocity.z = (proj.vz / speed) * proj.knockback;

    // Visual feedback: flash white (timer-based, works alongside AO filter)
    if (target.sprite) {
      target.sprite._hitFlashTimer = 0.1;
    }

    // Audio
    if (this.audioSystem) {
      this.audioSystem.playHitSound(target, proj.attacker, finalDamage, !target.isNPC);
    }

    // HUD damage number
    if (this.combatManager && this.combatManager.hudManager) {
      this.combatManager.hudManager.showDamageNumber(target, finalDamage);
    }

    // Notify AI of attacker position for threat-based targeting
    if (target.isNPC && target.behavior && target.behavior.setThreatPosition) {
      const ax = proj.attacker?.x ?? proj.impostor.x;
      const az = proj.attacker?.z ?? proj.impostor.z;
      target.behavior.setThreatPosition(ax, az);
    }

    // Death or hit reaction
    if (stats.health <= 0) {
      stats.health = 0;
      if (this.combatManager) {
        this.combatManager._handleDeath(target, proj.attacker);
      }
    } else {
      const name = target.characterName;
      let hitAnim = 'taking-punch';
      if (name === 'bear') hitAnim = 'going-to-sleep';
      else if (name === 'hellrat') hitAnim = 'eating';
      target.triggerAction(hitAnim);
    }
  }

  /**
   * Destroy a projectile (remove impostor from world, delete from map).
   * @private
   */
  _destroyProjectile(id, proj, impact = false, hitCuboid = null) {
    // Stop the in-flight whoosh
    if (proj.flySound) {
      proj.flySound.stop();
      proj.flySound = null;
    }

    if (impact) {
      const imp = proj.impostor;
      const pType = proj.projectileType;
      // Explosion sound at impact position
      if (this.audioSystem) {
        this.audioSystem.playFireballExplosion(imp.x, imp.y, imp.z);
      }
      // Type-specific explosion appearance
      if (pType === 'arrow') {
        // Small spark burst
        Object.assign(imp.element.style, {
          backgroundImage: 'none',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #fff 0%, #ffd700 50%, transparent 100%)',
          boxShadow: '0 0 12px 6px rgba(255, 200, 0, 0.5)'
        });
        imp.scale = 0.2;
      } else if (pType === 'ball') {
        // Small dust puff
        Object.assign(imp.element.style, {
          backgroundImage: 'none',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #d4c4a8 0%, #8B7355 50%, transparent 100%)',
          boxShadow: '0 0 8px 4px rgba(139, 115, 85, 0.4)'
        });
        imp.scale = 0.2;
      } else if (pType === 'boomerang') {
        // Small wooden burst
        Object.assign(imp.element.style, {
          backgroundImage: 'none',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #e8d070 0%, #8B6914 50%, transparent 100%)',
          boxShadow: '0 0 10px 5px rgba(139, 105, 20, 0.4)'
        });
        imp.scale = 0.2;
      } else if (pType === 'icebolt') {
        // Ice shatter: white-blue burst with frost shards
        Object.assign(imp.element.style, {
          backgroundImage: 'none',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #ffffff 0%, #aaeeff 30%, #3399ff 60%, transparent 100%)',
          boxShadow: '0 0 40px 20px rgba(100, 200, 255, 0.8)'
        });
        imp.scale = 0.45;
      } else if (pType === 'poisonbolt') {
        // Poison splash: green toxic burst with mist
        Object.assign(imp.element.style, {
          backgroundImage: 'none',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #eeffaa 0%, #66dd22 35%, #228800 65%, transparent 100%)',
          boxShadow: '0 0 36px 18px rgba(50, 200, 50, 0.7)'
        });
        imp.scale = 0.4;
      } else if (pType === 'arcanebolt') {
        // Arcane burst: purple/violet magical explosion
        Object.assign(imp.element.style, {
          backgroundImage: 'none',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #ffffff 0%, #dd99ff 30%, #8833dd 60%, transparent 100%)',
          boxShadow: '0 0 44px 22px rgba(160, 80, 255, 0.8)'
        });
        imp.scale = 0.45;
      } else if (pType === 'lightningbolt') {
        // Lightning discharge: bright yellow-white electric burst
        Object.assign(imp.element.style, {
          backgroundImage: 'none',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #ffffff 0%, #ffff66 25%, #ffcc00 50%, #ff8800 75%, transparent 100%)',
          boxShadow: '0 0 52px 26px rgba(255, 240, 60, 0.9)'
        });
        imp.scale = 0.55;
      } else {
        // Fireball explosion (default)
        Object.assign(imp.element.style, {
          background: 'radial-gradient(circle, #fff 0%, #ffff00 35%, #ff4400 65%, transparent 100%)',
          boxShadow: '0 0 48px 24px rgba(255, 160, 0, 0.9)'
        });
        imp.scale = 0.5;
      }
      // Keep the 0.9×0.9 XZ footprint — same as during flight — so the explosion
      // participates in the same SAT comparisons against nearby wall cuboids.
      // Reset sort centre so _setSortCenterOutsideWall can place it correctly.
      imp.sortCenterX = null;
      imp.sortCenterZ = null;
      // For wall hits: snap visual X/Z to the wall face and push the sort centre
      // outside the wall so SAT gives a clean separation verdict.
      if (hitCuboid) {
        this._setSortCenterOutsideWall(imp, hitCuboid, proj.vx, proj.vz);
      }
      // Force a re-sort immediately so the explosion appears in the right DOM position.
      if (this.world) this.world.requestFullSort();

      const capturedId = id;
      const shortExplosions = ['arrow', 'ball', 'boomerang'];
      const explosionDuration = shortExplosions.includes(pType) ? 60 : 90;
      const timeoutHandle = setTimeout(() => {
        this._explosionTimeouts.delete(capturedId);
        if (this.world) this.world.removeImpostor(capturedId);
      }, explosionDuration);
      this._explosionTimeouts.set(capturedId, timeoutHandle);
    } else {
      this.world.removeImpostor(id);
    }
    this.projectiles.delete(id);
  }

  /**
   * Remove all projectiles and pending explosions (e.g., on level transition).
   */
  clearAll() {
    for (const [id, proj] of this.projectiles) {
      if (proj.flySound) { proj.flySound.stop(); proj.flySound = null; }
      this.world.removeImpostor(id);
    }
    this.projectiles.clear();

    // Cancel and clean up any pending explosion timeouts — these are impostors
    // that were already removed from this.projectiles but still linger in the
    // world for 90ms as an explosion visual. Without this, they'd persist until
    // the orphaned setTimeout fires (or forever if the world was cleared).
    for (const [impostorId, handle] of this._explosionTimeouts) {
      clearTimeout(handle);
      if (this.world) this.world.removeImpostor(impostorId);
    }
    this._explosionTimeouts.clear();
  }
}
